import React from "react";
import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    FormGroup,
    IconButton,
    Snackbar,
    Stack,
    TextField,
} from "@mui/material";
import {useQueryClient} from "@tanstack/react-query";
import {useCustomMutation, useCustomQuery} from "../../../query/queryClient";
import NoteAltOutlinedIcon from '@mui/icons-material/NoteAltOutlined';
import AdminPanelSettings from '@mui/icons-material/AdminPanelSettings';
import type {Bag, DataBagSerializer, DataBagStaffApi} from "~/services/classes/Bag.ts";
import {useStores} from "../../../stores";
import {RemoveCircle, VisibilityOff} from "@mui/icons-material";
import moment from "moment/moment";

type BagInternalApi = Pick<DataBagSerializer & DataBagStaffApi, 'notes' | 'name' | 'hidden' | 'disabled'>

type Props = {
    bag: Bag
};
export const DatabagAdminEditModal: React.FC<Props> = ({bag}) => {
    const queryClient = useQueryClient();
    const {bagStore} = useStores();

    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
    const noteInputRef = React.useRef<HTMLTextAreaElement | null>(null);
    const open = Boolean(anchorEl);

    const bagId = bag.id;
    const url = `/bag/${bagId}/internal/`
    const internalQueryKey = [url] as const;
    const handleSave = useCustomMutation<BagInternalApi>();
    const {data, ...savedNoteReq} = useCustomQuery<BagInternalApi>({
        queryKey: internalQueryKey,
        enabled: open && bagId > 0,
    });

    const setInternalInCache = (patch: Partial<BagInternalApi>) => {
        queryClient.setQueryData<BagInternalApi>(internalQueryKey, previous => ({
            notes: previous?.notes ?? '',
            name: previous?.name ?? '',
            hidden: previous?.hidden ?? false,
            disabled: previous?.disabled ?? false,
            ...patch,
        }));
    };

    const syncBagStore = (cached: BagInternalApi) => {
        if (bagStore.allBags) {
            bagStore.setAllBags(bagStore.allBags.map(b =>
                b.id === bagId ? {...b, name: cached.name, hidden: cached.hidden, disabled: cached.disabled} : b
            ));
        }
        if (bagStore.bag?.id === bagId) {
            bagStore.setBag({...bagStore.bag, name: cached.name, hidden: cached.hidden, disabled: cached.disabled});
        }
    };

    const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
        queryClient.removeQueries({queryKey: internalQueryKey});
    };

    const [showSnackbar, setShowSnackbar] = React.useState(false);
    const handleSubmit = () => {
        const cached = queryClient.getQueryData<BagInternalApi>(internalQueryKey);
        if (!cached) {
            return;
        }

        const data: BagInternalApi = {
            notes: (cached.notes || '').trim(),
            name: (cached.name || '').trim(),
            hidden: Boolean(cached.hidden),
            disabled: Boolean(cached.disabled),
        };

        handleSave.mutate(
            {
                url: url,
                method: "PATCH",
                data,
            },
            {
                onSuccess: () => {
                    syncBagStore(data);
                    handleClose();
                    setShowSnackbar(true);
                },
            },
        );
    };

    const isLoading = savedNoteReq.isPending && data === undefined;
    const noteText = data?.notes || '';
    const nameText = data?.name || '';
    const hiddenValue = data?.hidden ?? false;
    const disabledValue = data?.disabled ?? false;

    return <>
        <Stack direction="row" alignItems="center">
            <AdminPanelSettings/>
            <IconButton color={"primary"} onClick={handleOpen}>
                <NoteAltOutlinedIcon/>
            </IconButton>
        </Stack>
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
            <DialogTitle><AdminPanelSettings/>Admin databag settings</DialogTitle>
            <DialogContentText/>
            <DialogContent>
                <Stack spacing={2}>
                    <Stack direction="row" alignItems="center">
                        <TextField label="Databag id" value={'' + bag.id} variant="standard" disabled/>
                        <TextField label="Created" value={moment(bag.timestamp).format('YYYY-MM-DD HH:mm')}
                                   variant="standard" disabled/>
                        <TextField label="Sample of" value={'' + bag.is_sample_of} variant="standard" disabled/>
                        <TextField label="Synergy family" value={'' + bag.synergy_family} variant="standard" disabled/>
                    </Stack>
                    <TextField
                        id="databag-name"
                        label="Databag name"
                        fullWidth
                        value={nameText}
                        onChange={e => setInternalInCache({name: e.target.value})}
                        disabled={isLoading}
                    />
                    <TextField
                        id="databag-note"
                        label="Note"
                        multiline
                        minRows={10}
                        maxRows={20}
                        fullWidth
                        value={noteText}
                        onChange={e => setInternalInCache({notes: e.target.value})}
                        inputRef={noteInputRef}
                        disabled={isLoading}
                    />
                    <Stack direction="row" spacing={4}>
                        <FormGroup>
                            <FormControlLabel
                                control={<Checkbox
                                    checked={hiddenValue}
                                    onChange={e => setInternalInCache({hidden: e.target.checked})}
                                    disabled={isLoading}
                                />}
                                label={<><VisibilityOff fontSize="small"/> Hidden</>}
                            />
                        </FormGroup>
                        <FormGroup>
                            <FormControlLabel
                                control={<Checkbox
                                    checked={disabledValue}
                                    onChange={e => setInternalInCache({disabled: e.target.checked})}
                                    disabled={isLoading}
                                />}
                                label={<><RemoveCircle fontSize="small"/> Disabled (Optimization for data
                                    processing)</>}
                            />
                        </FormGroup>
                    </Stack>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button sx={{mr: 2}} onClick={handleClose}>Cancel</Button>
                <Button
                    variant="contained"
                    disabled={handleSave.isPending}
                    onClick={handleSubmit}
                    loading={handleSave.isPending}>
                    Save
                </Button>
            </DialogActions>
        </Dialog>
        <Snackbar
            anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
            open={showSnackbar}
            onClose={() => setShowSnackbar(false)}
            message="Databag updated"
        />
    </>
}
